import GCPIcon from '@/assets/gcp.svg';
import AzureIcon from '@/assets/azure.svg';
import ByocIcon from '@/assets/byoc-label.svg';
import { ByocSelected, AWS } from '@/pages/home/icons';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { CloudProviderT } from '@/pages/admin/settings/cloud_provider/types';
import type { TGVersion } from '@/pages/workgroup/type';
import { compare, validate } from 'compare-versions';

export enum CloudPlatform {
  GCP = 'GCP',
  AWS = 'AWS',
  Azure = 'AZURE',
}

// All platforms the portal knows about, in display order.
export const SUPPORTED_PLATFORMS: CloudPlatform[] = [CloudPlatform.AWS, CloudPlatform.GCP, CloudPlatform.Azure];

interface Props extends Partial<CloudProviderT> {
  selected: boolean;
  disabled: boolean;
  onSelect?: () => void;
}

const cloudProviderIconMap: Record<
  CloudPlatform,
  { name: string; label: string; icon?: string; Component?: typeof AWS }
> = {
  GCP: {
    name: 'GCP',
    label: 'GCP',
    icon: GCPIcon,
  },
  AWS: {
    name: 'AWS',
    label: 'AWS',
    Component: AWS,
  },
  AZURE: {
    name: 'AZURE',
    label: 'Azure',
    icon: AzureIcon,
  },
};

/**
 * The backend normalizes cloud provider platform strings to lower case (e.g. 'aws'), while
 * {@link CloudPlatform} and the UI use upper case. Use this for both comparison and display.
 */
export function normalizePlatform(platform?: string): string {
  return (platform || '').toUpperCase();
}

/**
 * Platforms are rolled out by deploying their public cloud providers, which happens after the
 * controller/portal release. A platform is available once at least one public cloud provider
 * exists for it, so new platforms light up without portal changes.
 */
export function getAvailablePlatforms(
  publicCPs: Pick<CloudProviderT, 'platform'>[],
  // When provided, platforms without a supported TigerGraph version are also unavailable.
  tgVersions?: Pick<TGVersion, 'name'>[]
): Set<string> {
  return new Set(
    publicCPs
      .map((cp) => normalizePlatform(cp.platform))
      .filter((platform) => !tgVersions || hasSupportedTGVersion(platform, tgVersions))
  );
}

// The minimum TigerGraph version a platform supports. Platforms not listed support every version.
const PLATFORM_MIN_TG_VERSION: Partial<Record<CloudPlatform, string>> = {
  [CloudPlatform.GCP]: '4.3.0',
};

export function isTGVersionSupportedOnPlatform(version: string, platform?: string): boolean {
  const minVersion = PLATFORM_MIN_TG_VERSION[normalizePlatform(platform) as CloudPlatform];
  if (!minVersion) {
    return true;
  }
  // Cloud versions may include a build suffix (e.g. 4.3.0-12110523); compare the release part only.
  const release = version.split('-')[0];
  return validate(release) && compare(release, minVersion, '>=');
}

/**
 * A platform with a minimum TigerGraph version is only usable once at least one supported version
 * is offered, otherwise no workspace could be created on it.
 */
export function hasSupportedTGVersion(platform: string, tgVersions: Pick<TGVersion, 'name'>[]): boolean {
  if (!PLATFORM_MIN_TG_VERSION[normalizePlatform(platform) as CloudPlatform]) {
    return true;
  }
  return tgVersions.some((v) => isTGVersionSupportedOnPlatform(v.name, platform));
}

export function getPlatformLabel(platform?: string): string {
  return cloudProviderIconMap[normalizePlatform(platform) as CloudPlatform]?.label || platform || '';
}

export function PlatformIcon({
  platform = 'AWS',
  height,
  width,
}: {
  platform?: string;
  height?: number;
  width?: number;
}) {
  platform = normalizePlatform(platform);
  const [css] = useStyletron();
  const Component = cloudProviderIconMap[platform as CloudPlatform].Component;

  return Component ? (
    <Component width={width} height={height} />
  ) : (
    <img
      className={css({
        height: `${height ? `${height}px` : 'auto'}`,
        width: `${width ? `${width}px` : 'auto'}`,
      })}
      src={cloudProviderIconMap[platform as CloudPlatform].icon as string}
      alt={cloudProviderIconMap[platform as CloudPlatform].name}
    />
  );
}

export default function CloudProviderCategory({ selected, disabled, onSelect, ...cloudProvider }: Props) {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        height: '84px',
        boxSizing: 'border-box',
        borderWidth: '2px',
        borderColor: selected ? theme.colors['card.border.selected'] : theme.colors['card.border'],
        position: 'relative',
      })}
      onClick={() => {
        if (!disabled) {
          onSelect?.();
        }
      }}
    >
      {selected && (
        <div className={css({ position: 'absolute', top: '-1px', right: '-2px' })}>
          <ByocSelected />
        </div>
      )}
      {cloudProvider.type !== 'public' && (
        <img src={ByocIcon} alt="byoc" className={css({ position: 'absolute', top: '0', left: '0' })} />
      )}
      <div
        className={css({
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <PlatformIcon platform={cloudProvider.platform} />
      </div>
      <div
        className={css({
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: '16px',
          textAlign: 'center',
          marginTop: '8px',
          color: theme.colors['card.text.primary'],
        })}
      >
        {cloudProvider.type === 'public' ? (
          cloudProvider.comingSoon ? (
            <span>
              {cloudProviderIconMap[normalizePlatform(cloudProvider.platform) as CloudPlatform]?.label}{' '}
              <span
                className={css({
                  fontSize: '12px',
                  fontWeight: 400,
                })}
              >
                (Coming Soon)
              </span>
            </span>
          ) : (
            normalizePlatform(cloudProvider.platform)
          )
        ) : (
          cloudProvider.name
        )}
      </div>
    </div>
  );
}
